import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Pager, usePaged } from '../components/Pager';
import { SortHeader } from '../components/SortHeader';
import { EmptyState } from '../components/EmptyState';
import { ScannedAt } from '../components/ScannedAt';
import { compareBy, useUrlNumber, useUrlSort, useUrlState } from '../lib/useUrlState';
import { RefreshButton } from '../components/RefreshButton';

const SUBNETS_PER_PAGE = 20;

/**
 * Network → Subnets. The WireGuard overlay carve-up plus live IP usage.
 *
 * Ported from optisigns-staff-app (`features/network/SubnetsPage` + `SubnetRow`).
 * Source of the definitions is the SYNCED COPY of wg-orchestrator's network.yaml
 * (apps/api/src/config/network.yaml — resync rule in its header); usage comes
 * from the live `ipam_counters` collection wg-orchestrator maintains.
 */

const VPN_SUBNETS = gql`
  query VpnSubnets {
    vpnSubnets {
      name
      cidr
      type
      allowProvisioner
      used
      total
    }
  }
`;

interface Subnet {
  name: string;
  cidr: string;
  type: string;
  allowProvisioner: boolean;
  used: number;
  total?: number | null;
}

/** Fraction of the subnet consumed, or null when it has no seeded IPAM counter.
 *  Null (not 0) so an unbounded subnet sorts LAST under "most full" rather than
 *  looking like the emptiest one. */
function usagePct(s: Subnet): number | null {
  return typeof s.total === 'number' && s.total > 0 ? (s.used / s.total) * 100 : null;
}

const SORT_PICK: Record<string, (s: Subnet) => unknown> = {
  name: (s) => s.name,
  cidr: (s) => s.cidr,
  type: (s) => s.type,
  allowProvisioner: (s) => s.allowProvisioner,
  usage: usagePct,
};

// Matches every other page's Stat — see the note in VpnPeersPage.
function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="stat">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
}

export function VpnSubnetsPage() {
  const { data, loading, error, refetch } = useQuery<{ vpnSubnets: Subnet[] }>(VPN_SUBNETS, {
    fetchPolicy: 'cache-and-network',
  });
  // Filter / sort / page in the URL, same contract as the inventory tables: a
  // narrowed view is a link you can paste.
  const [q, setQ] = useUrlState('q');
  const [typeFilter, setTypeFilter] = useUrlState('type');
  const sort = useUrlSort('name');
  const [page, setPage] = useUrlNumber('page', 1);

  const subnets = data?.vpnSubnets ?? [];
  const types = [...new Set(subnets.map((s) => s.type))].sort();
  const needle = q.trim().toLowerCase();
  const filtered = subnets.filter(
    (s) =>
      (!needle || s.name.toLowerCase().includes(needle) || s.cidr.toLowerCase().includes(needle)) &&
      (!typeFilter || s.type === typeFilter),
  );
  const sorted = compareBy(filtered, (s) => SORT_PICK[sort.key]?.(s), sort.dir);
  const pg = usePaged(sorted, SUBNETS_PER_PAGE, { page, onPage: setPage });

  if (loading && !data)
    return (
      <div className="page">
        <div className="skeleton-row-group">
          {[0, 1, 2, 3].map((i) => (
            <div className="skeleton-row" key={i} />
          ))}
        </div>
      </div>
    );
  // Phase-2 error copy: what failed, what it means, who to ask — and no
  // environment variable names. The old text named the Mongo connection string,
  // which is neither actionable for the reader nor the usual cause.
  if (error)
    return (
      <div className="page">
        <p className="error">Couldn&apos;t load the subnets — the API didn&apos;t respond.</p>
        <p className="muted">
          Nothing changed on the VPN; this view only reads. If it persists, ask in #devops-infra.
        </p>
      </div>
    );

  const personCount = subnets.filter((s) => s.type === 'person').length;
  const totalUsed = subnets.reduce((sum, s) => sum + (s.used || 0), 0);
  const anyFilter = Boolean(needle || typeFilter);

  return (
    <div className="page">
      <p className="muted">
        Overlay carve-up from wg-orchestrator&apos;s <code>network.yaml</code>, joined with live
        IPAM counters. Read-only.{' '}
        {/* A new tab on purpose: the reader is usually mid-way through this table. */}
        <a href="/docs/vpn-subnets" target="_blank" rel="noopener noreferrer">
          Read more — what subnets are and who can reach what ↗
        </a>
      </p>
      {/* Not scanned into Postgres like the inventory tables — read live on every
          page load. Saying so is the §2 "how old is this?" line for this page. */}
      <ScannedAt curated lead="Read live from wg-orchestrator on every load — not a scan, so this is current." />

      <div className="stat-row">
        <Stat label="Subnets" value={subnets.length} />
        <Stat label="Person subnets" value={personCount} />
        <Stat label="IPs allocated" value={totalUsed.toLocaleString()} />
      </div>

      <div className="filter-row">
        <RefreshButton refetch={() => refetch().catch(() => undefined)} />
        <input
          type="search"
          placeholder="Search name or CIDR…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} title="filter by type">
          <option value="">All types</option>
          {types.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <span className="filter-count">
          {filtered.length} of {subnets.length}
        </span>
      </div>

      {subnets.length === 0 ? (
        <EmptyState
          icon="cluster"
          title="No subnets"
          body="The overlay carve-up is read from wg-orchestrator's network.yaml. An empty list means the app couldn't reach it — ask in #devops-infra."
        />
      ) : filtered.length === 0 ? (
        <p className="muted">
          No subnet matches these filters.{' '}
          {anyFilter && (
            <button
              className="mini-btn"
              onClick={() => {
                setQ('');
                setTypeFilter('');
              }}
            >
              Clear filters
            </button>
          )}
        </p>
      ) : (
        <>
          <table className="data-list--primary">
            <thead>
              <tr>
                <SortHeader label="Name" sortKey="name" sort={sort} />
                <SortHeader label="CIDR / Range" sortKey="cidr" sort={sort} />
                <SortHeader label="Type" sortKey="type" sort={sort} />
                <SortHeader label="Provisioner?" sortKey="allowProvisioner" sort={sort} />
                <SortHeader label="Usage" sortKey="usage" sort={sort} title="Sort by how full the subnet is" />
              </tr>
            </thead>
            <tbody>
              {pg.pageItems.map((s) => (
                <SubnetRow key={s.name} subnet={s} />
              ))}
            </tbody>
          </table>
          {/* Renders nothing while the subnet count stays under one page — which
              it does today (6). Present so a future overlay carve-up pages. */}
          <Pager
            page={pg.page}
            pageCount={pg.pageCount}
            total={pg.total}
            start={pg.start}
            pageSize={pg.pageSize}
            onPage={pg.setPage}
          />
        </>
      )}
    </div>
  );
}

/**
 * One subnet row with a usage bar. `total` is absent for a subnet with no seeded
 * IPAM counter, in which case usage renders as "N / ∞" rather than a bar at 0%.
 */
function SubnetRow({ subnet }: { subnet: Subnet }) {
  const raw = usagePct(subnet);
  const finite = raw != null;
  const pct = finite ? Math.min(100, raw) : 0;

  return (
    <tr>
      <td>{subnet.name}</td>
      {/* A CIDR is a resource identifier, not a code snippet — Plex Sans per the
          os-internal-ui mono policy ratified in phase 6 §1. */}
      <td>
        <span className="text-xs">{subnet.cidr || '—'}</span>
      </td>
      <td>
        <span className="badge">{subnet.type}</span>
      </td>
      <td>
        {subnet.allowProvisioner ? <span>Yes</span> : <span className="muted">No</span>}
      </td>
      <td>
        <div className="dot-cell" style={{ gap: 10, minWidth: 180 }}>
          <div
            className={`usage-bar${pct >= 90 ? ' high' : pct >= 70 ? ' warn' : ''}`}
            title={finite ? `${pct.toFixed(1)}% of ${subnet.total?.toLocaleString()}` : 'unbounded'}
          >
            <span style={{ width: `${pct}%` }} />
          </div>
          <span className="muted text-xs" style={{ whiteSpace: 'nowrap' }}>
            {subnet.used.toLocaleString()} / {finite ? subnet.total?.toLocaleString() : '∞'}
          </span>
        </div>
      </td>
    </tr>
  );
}
